/* GenuinePurity — Offer LP kit (gpo). Loaded by layout/gpo.liquid.
   Hooks are data-gpo-* attributes only. Sections re-render in the theme
   editor, so everything is delegated or re-run on shopify:section:load. */
(function () {
  'use strict';

  /* ---------- Video: swap the cover for a YouTube/Vimeo iframe ----------- */
  function mountVideo(frame) {
    if (frame.querySelector('iframe')) return;
    var iframe = document.createElement('iframe');
    iframe.src = frame.getAttribute('data-gpo-video');
    iframe.title = frame.getAttribute('data-gpo-video-title') || 'Video';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write; web-share';
    iframe.allowFullscreen = true;
    // YouTube refuses to play (error 153) when the embed sends no referrer.
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.className = 'gpo-video-iframe is-cover';
    frame.appendChild(iframe);
    frame.classList.add('is-playing');
    fitVideo(frame, iframe.src);
  }

  // Ask Vimeo for the video's real shape so the cover crop is exact (vertical and
  // square videos included). Until it answers, the CSS assumes 16:9. YouTube has
  // no CORS oEmbed, so YouTube stays 16:9 (Shorts get 9:16 from the section).
  function fitVideo(frame, src, setFrame) {
    var m = /player\.vimeo\.com\/video\/(\d+)(?:[^#]*?[?&]h=([\w]+))?/.exec(src);
    if (!m || !window.fetch) return;
    var url = 'https://vimeo.com/' + m[1] + (m[2] ? '/' + m[2] : '');
    fetch('https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(url))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.width || !d.height) return;
        frame.style.setProperty('--gpo-vr', d.width / d.height);
        if (setFrame) frame.style.setProperty('--gpo-ratio', d.width + ' / ' + d.height);
      })
      .catch(function () {});
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gpo-video-play]');
    if (!btn) return;
    var frame = btn.closest('[data-gpo-video]');
    if (frame) mountVideo(frame);
  });

  // Autoplay videos load once they near the viewport.
  function initAutoplay(root) {
    var frames = root.querySelectorAll('[data-gpo-video][data-gpo-video-autoplay]');
    if (!frames.length) return;
    if (!('IntersectionObserver' in window)) { frames.forEach(mountVideo); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { mountVideo(entry.target); io.unobserve(entry.target); }
      });
    }, { rootMargin: '200px' });
    frames.forEach(function (f) { io.observe(f); });
  }

  /* ---------- Steps: timeline rail + swipe/drag card track --------------- */
  function initSteps(root) {
    root.querySelectorAll('[data-gpo-steps]').forEach(function (el) {
      if (el.gpoSteps) return;
      var track = el.querySelector('[data-gpo-track]');
      var slides = Array.prototype.slice.call(el.querySelectorAll('[data-gpo-slide]'));
      var steps = Array.prototype.slice.call(el.querySelectorAll('[data-gpo-step]'));
      if (!track || !slides.length) return;
      var current = -1;

      function stride() { return slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth; }

      function setActive(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        if (i === current) return;
        current = i;
        el.style.setProperty('--gpo-step', i);
        steps.forEach(function (s, n) {
          s.classList.toggle('is-active', n === i);
          s.classList.toggle('is-done', n < i);
          s.setAttribute('aria-selected', n === i ? 'true' : 'false');
          s.tabIndex = n === i ? 0 : -1;
        });
        slides.forEach(function (sl, n) { sl.setAttribute('aria-hidden', n === i ? 'false' : 'true'); });
      }

      function goTo(i, smooth) {
        setActive(i);
        track.scrollTo({ left: slides[current].offsetLeft - slides[0].offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
      }

      steps.forEach(function (s) {
        s.addEventListener('click', function () { goTo(+s.getAttribute('data-gpo-step')); });
        s.addEventListener('keydown', function (e) {
          var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          goTo(current + d);
          steps[current].focus();
        });
      });

      // Swipe (native scroll-snap) -> keep the rail in sync.
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking || dragging) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; setActive(Math.round(track.scrollLeft / stride())); });
      }, { passive: true });

      // Mouse drag (touch already scrolls natively).
      var dragging = false, startX = 0, startScroll = 0, moved = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true; moved = 0; startX = e.clientX; startScroll = track.scrollLeft;
        track.setPointerCapture(e.pointerId);
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        moved = e.clientX - startX;
        track.scrollLeft = startScroll - moved;
      });
      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        if (e && typeof e.clientX === 'number') moved = e.clientX - startX;
        track.classList.remove('is-dragging');
        var shift = Math.abs(moved) > 60 ? (moved < 0 ? 1 : -1) : 0;
        goTo(current + shift);
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', function () { endDrag(); });
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });

      window.addEventListener('resize', function () { if (current > -1) goTo(current, false); });

      el.gpoSteps = { goTo: goTo, blockIndex: function (id) {
        for (var n = 0; n < slides.length; n++) if (slides[n].getAttribute('data-gpo-block') === id) return n;
        return -1;
      } };
      setActive(0);
    });
  }

  /* ---------- Drag: mouse drag for any scroll-snap track ---------------- */
  // Touch already swipes natively; this adds click-and-drag for mouse users.
  // On release it snaps to the nearest item, and swallows the click a drag ends with.
  function initDrag(root) {
    root.querySelectorAll('[data-gpo-drag]').forEach(function (track) {
      if (track.gpoDrag) return;
      track.gpoDrag = true;
      var down = false, startX = 0, startScroll = 0, moved = 0;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = 0; startX = e.clientX; startScroll = track.scrollLeft;
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        moved = e.clientX - startX;
        if (Math.abs(moved) > 5 && !track.classList.contains('is-dragging')) {
          track.classList.add('is-dragging');
          track.setPointerCapture(e.pointerId);
        }
        if (track.classList.contains('is-dragging')) track.scrollLeft = startScroll - moved;
      });
      function end(e) {
        if (!down) return;
        down = false;
        if (e && typeof e.clientX === 'number') moved = e.clientX - startX;
        if (Math.abs(moved) <= 5) { track.classList.remove('is-dragging'); return; }
        if (!track.classList.contains('is-dragging')) track.scrollLeft = startScroll - moved;
        // Snap to the item nearest the track's start edge.
        var items = track.children;
        var best = 0, bestD = Infinity;
        for (var i = 0; i < items.length; i++) {
          var d = Math.abs(items[i].offsetLeft - items[0].offsetLeft - track.scrollLeft);
          if (d < bestD) { bestD = d; best = i; }
        }
        track.classList.remove('is-dragging');
        track.scrollTo({ left: items[best].offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
        track.gpoJustDragged = true;
        setTimeout(function () { track.gpoJustDragged = false; }, 0);
      }
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', function () { end(); });
      track.addEventListener('click', function (e) {
        if (track.gpoJustDragged) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  /* ---------- Reels: one Shopify-hosted <video> plays at a time ----------- */
  function stopReel(reel) {
    var v = reel.querySelector('video');
    if (v) { v.pause(); v.controls = false; }
    reel.classList.remove('is-playing');
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gpo-reel-play]');
    if (!btn) return;
    var reel = btn.closest('[data-gpo-reel]');
    var v = reel && reel.querySelector('video');
    if (!v) return;
    document.querySelectorAll('[data-gpo-reel].is-playing').forEach(function (r) { if (r !== reel) stopReel(r); });
    reel.classList.add('is-playing');
    v.controls = true;
    var p = v.play();
    if (p && p.catch) p.catch(function () { stopReel(reel); });
    if (!v.gpoBound) {
      v.gpoBound = true;
      v.addEventListener('ended', function () { stopReel(reel); v.currentTime = 0; });
    }
  });

  /* ---------- Gallery: arrows, counter and thumbs for a swipe/drag track -- */
  function initGallery(root) {
    root.querySelectorAll('[data-gpo-gallery]').forEach(function (g) {
      if (g.gpoGallery) return;
      g.gpoGallery = true;
      var track = g.querySelector('[data-gpo-gallery-track]');
      var slides = track ? track.children : [];
      if (slides.length < 2) return;
      var thumbs = g.querySelectorAll('[data-gpo-gallery-thumb]');
      var index = g.querySelector('[data-gpo-gallery-index]');
      var current = 0, lockUntil = 0;
      function go(i) {
        i = (i + slides.length) % slides.length;
        mark(i);
        lockUntil = Date.now() + 700; // ignore scroll events while the smooth scroll runs
        track.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      }
      function mark(i) {
        if (i === current) return;
        current = i;
        if (index) index.textContent = i + 1;
        thumbs.forEach(function (t, n) {
          t.classList.toggle('is-active', n === i);
          if (n === i) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });
      }
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking || Date.now() < lockUntil) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; mark(Math.round(track.scrollLeft / track.clientWidth)); });
      }, { passive: true });
      var prev = g.querySelector('[data-gpo-gallery-prev]'), next = g.querySelector('[data-gpo-gallery-next]');
      if (prev) prev.addEventListener('click', function () { go(current - 1); });
      if (next) next.addEventListener('click', function () { go(current + 1); });
      thumbs.forEach(function (t) { t.addEventListener('click', function () { go(+t.getAttribute('data-gpo-gallery-thumb')); }); });
    });
  }

  /* ---------- Buy box: live prices + add to cart -> checkout ------------- */
  // Prices come from the Liquid-built map (key "qty-plan", cents), so the page
  // always shows what checkout will charge. Unwired pairs keep the typed text.
  function initBuy(root) {
    root.querySelectorAll('[data-gpo-buy]').forEach(function (box) {
      if (box.gpoBuy) return;
      var cfgEl = box.querySelector('[data-gpo-buy-config]');
      if (!cfgEl) return;
      box.gpoBuy = true;
      var cfg = JSON.parse(cfgEl.textContent);
      var qtys = Array.prototype.slice.call(box.querySelectorAll('[data-gpo-qty]'));
      var plans = Array.prototype.slice.call(box.querySelectorAll('[data-gpo-plan]'));
      var btn = box.querySelector('[data-gpo-atc]');
      var err = box.querySelector('[data-gpo-atc-error]');
      var label = box.querySelector('[data-gpo-atc-label]');
      var labelText = label ? label.textContent : '';

      function money(cents) {
        var whole = cents % 100 === 0;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cfg.currency || 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100);
        } catch (e) { return '$' + (cents / 100).toFixed(whole ? 0 : 2); }
      }
      function idx(list) { for (var i = 0; i < list.length; i++) if (list[i].checked) return i; return 0; }
      function price(q, p) { return cfg.prices[(q + 1) + '-' + (p + 1)]; }
      function pct(pr) { return pr && pr.compare > pr.price ? Math.round((1 - pr.price / pr.compare) * 100) : 0; }
      // A template that needs a percent shows nothing when there is no discount (never "0% off").
      function fill(tpl, pr) {
        tpl = tpl || '';
        if (tpl.indexOf('[percent]') > -1 && !pct(pr)) return '';
        return tpl.replace(/\[percent\]/g, pct(pr));
      }
      // Once anything is wired, a pair with no Shopify price shows a dash, never a stale number.
      var wired = Object.keys(cfg.prices).length > 0, DASH = '—';
      function setAll(sel, text, scope) {
        (scope || box).querySelectorAll(sel).forEach(function (el) { el.textContent = text; el.hidden = !text; });
      }

      function render() {
        var q = idx(qtys), p = idx(plans);
        var planEl = plans[p];
        // Per-bottle prices on the quantity cards, for the chosen plan.
        qtys.forEach(function (input, i) {
          var pr = price(i, p);
          var card = input.closest('label');
          if (!pr) {
            if (wired) { setAll('[data-gpo-unit-price]', DASH, card); setAll('[data-gpo-unit-compare]', '', card); }
            return;
          }
          var bottles = +input.getAttribute('data-bottles') || 1;
          setAll('[data-gpo-unit-price]', money(Math.round(pr.price / bottles)), card);
          setAll('[data-gpo-unit-compare]', pr.compare > pr.price ? money(Math.round(pr.compare / bottles)) : '', card);
        });
        // Plan totals, for the chosen quantity.
        plans.forEach(function (input, j) {
          var pr = price(q, j);
          var card = input.closest('label');
          if (!pr) {
            if (wired) {
              setAll('[data-gpo-plan-price]', DASH, card);
              setAll('[data-gpo-plan-compare]', '', card);
              card.querySelectorAll('[data-gpo-plan-badge]').forEach(function (el) { el.hidden = true; });
            }
            return;
          }
          setAll('[data-gpo-plan-price]', money(pr.price), card);
          setAll('[data-gpo-plan-compare]', pr.compare > pr.price ? money(pr.compare) : '', card);
          card.querySelectorAll('[data-gpo-plan-badge]').forEach(function (el) { el.textContent = fill(el.getAttribute('data-template'), pr); el.hidden = !el.textContent; });
        });
        var gifted = planEl && planEl.getAttribute('data-gift') === 'true';
        var gifts = box.querySelector('[data-gpo-gifts]');
        if (gifts) gifts.classList.toggle('is-muted', !gifted);
        var pr = price(q, p);
        if (planEl) {
          setAll('[data-gpo-deal]', fill(planEl.getAttribute('data-deal'), pr));
          setAll('[data-gpo-deal-sub]', planEl.getAttribute('data-deal-sub') || '');
        }
        if (btn) btn.disabled = !(pr && pr.live);
        if (!pr) {
          if (wired) {
            setAll('[data-gpo-total]', DASH);
            setAll('[data-gpo-total-compare]', '');
            setAll('[data-gpo-save]', '');
          }
          return;
        }
        setAll('[data-gpo-total]', money(pr.price));
        setAll('[data-gpo-total-compare]', pr.compare > pr.price ? money(pr.compare) : '');
        var tpl = gifted ? cfg.saveTemplate : cfg.saveTemplatePlain;
        if (tpl) {
          var saved = Math.max(0, pr.compare - pr.price) + (gifted ? cfg.giftValue + cfg.shipValue : 0);
          setAll('[data-gpo-save]', saved > 0 ? tpl.replace('[amount]', money(saved)) : '');
        } else {
          setAll('[data-gpo-save]', '');
        }
      }

      function fail(msg) {
        if (err) { err.textContent = msg; err.hidden = false; }
        btn.disabled = false;
        if (label) label.textContent = labelText;
      }

      function post(url, body) {
        return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
          .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || 'Could not add to cart.'); return j; }); });
      }

      if (btn) btn.addEventListener('click', function () {
        var q = idx(qtys), p = idx(plans);
        var qEl = qtys[q], pEl = plans[p];
        var chosen = price(q, p);
        if (!qEl || !pEl || !chosen || !chosen.live) return;
        if (err) err.hidden = true;
        btn.disabled = true;
        if (label && cfg.busy) label.textContent = cfg.busy;

        var variant = +qEl.getAttribute('data-variant');
        var line = { id: variant, quantity: +qEl.getAttribute('data-cart-qty') || 1 };
        var isSub = pEl.getAttribute('data-type') === 'subscription';
        if (isSub) line.selling_plan = +(qEl.getAttribute('data-sub-plan') || pEl.getAttribute('data-plan'));
        var gifted = pEl.getAttribute('data-gift') === 'true';
        var code = isSub ? '' : (qEl.getAttribute('data-onetime-code') || '');
        if (gifted && cfg.giftCode) code = code ? code + ',' + cfg.giftCode : cfg.giftCode;
        var items = [line];
        if (gifted && cfg.gift) {
          items.push({ id: +cfg.gift, quantity: 1, properties: { _gpo_gift: 'free-with-subscription' } });
        }
        // Clear this offer's earlier lines (a back-button retry must not double the order); keep anything else.
        var ours = qtys.map(function (i) { return +i.getAttribute('data-variant'); }).concat(cfg.gift ? [+cfg.gift] : []);
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var updates = {};
            cart.items.forEach(function (it) { if (ours.indexOf(it.variant_id) > -1) updates[it.key] = 0; });
            return Object.keys(updates).length ? post('/cart/update.js', { updates: updates }) : null;
          })
          .then(function () { return post('/cart/add.js', { items: items }); })
          .then(function () { window.location.href = '/checkout' + (code ? '?discount=' + encodeURIComponent(code) : ''); })
          .catch(function (e) { fail(e.message || 'Something went wrong. Please try again.'); });
      });

      qtys.concat(plans).forEach(function (input) { input.addEventListener('change', render); });
      render();
    });
  }

  // "Match the video" frames take the video's shape before anyone presses play.
  function initVideoFit(root) {
    root.querySelectorAll('[data-gpo-video][data-gpo-video-fit="auto"]').forEach(function (f) {
      fitVideo(f, f.getAttribute('data-gpo-video'), true);
    });
  }

  initVideoFit(document);
  initAutoplay(document);
  initSteps(document);
  initDrag(document);
  initGallery(document);
  initBuy(document);
  document.addEventListener('shopify:section:load', function (e) { initVideoFit(e.target); initAutoplay(e.target); initSteps(e.target); initDrag(e.target); initGallery(e.target); initBuy(e.target); });
  // Theme editor: selecting an FAQ question opens it.
  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.matches && e.target.matches('details')) e.target.open = true;
  });
  // Theme editor: selecting a stage block shows its card.
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.closest('[data-gpo-steps]') || (e.target.closest('.shopify-section') || e.target).querySelector('[data-gpo-steps]');
    if (!el || !el.gpoSteps) return;
    var i = el.gpoSteps.blockIndex(e.detail.blockId);
    if (i > -1) el.gpoSteps.goTo(i);
  });
})();
